/* OSManagementComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");
const stringifyCSV = require("csv-string").stringify;

const Renderer = require("./OSManagementRenderer");
const FileName = require("@gluenetworks/steve_utilities").fileName;
const {
    CATALOG_USAGE,
    DEFAULT_PORTS,
    SSH_MAC_DEF,
    SSH_KEX_DEF,
    SSH_CIPHER_DEF,
} = require("./OSManagementConstants");

const SYS_ADMIN_GROUP = "System Admin";
const SYS_DEV_GROUP = "System Developer";
const groupsAllowedToGenerateKeys = [SYS_ADMIN_GROUP, SYS_DEV_GROUP];

/**
 * @class OSManagementComponent
 * @extends EventEmitter
 *
 * @fires OSManagementComponent#Shutdown
 * @fires OSManagementComponent#DataUpdated
 * @fires OSManagementComponent#DirtyState
 * @fires OSManagementComponent#OrganizationDataChange
 */

class OSManagementComponent extends EventEmitter {
    constructor(
        OrganizationService,
        userSession,
        logger,
        userFeedback,
        OsImageServerPrefsEntity,
        OSUpgradePlanPrefsEntity,
        OSImageServer,
        userService,
        steveIo,
        uiUtils,
        VendorApiConfig,
        OsUpgradeOptions,
        ValueMapEntity,
        router,
        CredentialsEntity
    ) {
        super();
        this._OrganizationService = OrganizationService;
        this._userSession = userSession;
        this._logger = logger;
        this._modalUserFeedback = userFeedback;
        this._OsImageServerPrefsEntity = OsImageServerPrefsEntity;
        this._OsUpgradePlanPrefsEntity = OSUpgradePlanPrefsEntity;
        this._ValueMapEntity = ValueMapEntity;
        this._OsImageServersEntity = OSImageServer;
        this._OsUpgradeOptionsEntity = OsUpgradeOptions;
        this._osmOptionsAvailable = false;
        this._userService = userService;
        this._steveIo = steveIo;
        this._uiUtils = uiUtils;
        this._vendorApiConfigEntity = VendorApiConfig;
        this._isCiscoEnabled = undefined;
        this._router = router;
        this._credentialsEntity = CredentialsEntity;
        this._fileNamer = FileName;

        this._sshProps = {
            //SSH_MAC_DEF, SSH_KEX_DEF, SSH_CIPHER_DEF
            sshKexAlgorithms: SSH_KEX_DEF,
            sshCiphers: SSH_CIPHER_DEF,
            sshMACs: SSH_MAC_DEF,
            sshProtocolOneEnabled: false,
        };

        this._organizationData = {};
        /**
         * @type {OsUpgradePlanPrefsEntity}
         * @private
         */
        this._osUpgradePlanPrefs = undefined;

        /**
         * @type {OsImageServerPrefs}
         * @private
         */
        this._osImageServerPrefs = undefined;

        this._orgId = _.get(userSession, "activeOrganization.id");
        this._imageServersFeed = this._establishImageServersFeed(this._orgId);
        this.defaultProperties = {
            serverPrefs: {
                fileServersEnabled: false,
                protocols: {
                    ftp: true,
                    tftp: true,
                },
                ftpPort: DEFAULT_PORTS.FTP,
                scpPort: DEFAULT_PORTS.SCP,
                tftpPort: DEFAULT_PORTS.TFTP,
                adminPort: DEFAULT_PORTS.HOST,
                anonymousFtp: false,
                sshKexAlgorithms: SSH_KEX_DEF,
                sshCiphers: SSH_CIPHER_DEF,
                sshMACs: SSH_MAC_DEF,
                sshProtocolOneEnabled: false,
            },
            planPrefs: {
                enableCatalogs: false,
                skipCiscoApiImageChecks: false,
                catalogUsage: CATALOG_USAGE[0].value,
                catalogCreationAuthGroup: undefined,
                linear: false,
                allowMultipleExecutions: false,
                skipValidation: false,
                abortOnFailure: false,
                failureThreshold: 0,
            },
        };

        this._triggerDataUpdated = this._triggerDataUpdated.bind(this);
        this._onPrefsDirtyStateChange = this._onPrefsDirtyStateChange.bind(this);
        this._onOsUpgradePlanPrefsReplaced = this._onOsUpgradePlanPrefsReplaced.bind(this);
        this._onOsImageServerPrefsReplaced = this._onOsImageServerPrefsReplaced.bind(this);

        this._renderer = new Renderer(this);
    }

    get imageServersFeed() {
        return this._imageServersFeed;
    }

    get osPrefsData() {
        const prefsData = Object.assign(
            {},
            this._osUpgradePlanPrefs ? this._osUpgradePlanPrefs.getProperties() : {},
            this._osImageServerPrefs ? this._osImageServerPrefs.getProperties() : {}
        );
        return _.defaults(
            prefsData,
            this.defaultProperties.planPrefs,
            this.defaultProperties.serverPrefs
        );
    }

    get OsImageServersEntity() {
        return this._OsImageServersEntity;
    }

    get hasNoUpstreamFileServers() {
        const inheritedFileServers = !this.fileServersEnabled;
        return inheritedFileServers && _.isEmpty(this._imageServersFeed.cache);
    }

    get fileServersEnabled() {
        return (
            this._osImageServerPrefs &&
            this._osImageServerPrefs.tenantId === this.activeOrgId &&
            this._osImageServerPrefs.fileServersEnabled === true
        );
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _setOsUpgradePlanPrefs(osUpgradePrefs) {
        _.invoke(this._osUpgradePlanPrefs, "shutdown");
        this._osUpgradePlanPrefs = osUpgradePrefs;
        this._registerOsPrefsListeners(osUpgradePrefs, this._onOsUpgradePlanPrefsReplaced);
        return this;
    }
    _setOsImageServerPrefs(osImageServerPrefs) {
        _.invoke(this._osImageServerPrefs, "shutdown");
        this._osImageServerPrefs = osImageServerPrefs;
        this._registerOsPrefsListeners(osImageServerPrefs, this._onOsImageServerPrefsReplaced);
        return this;
    }

    _registerListeners() {}

    _unregisterListeners() {}

    _registerFeedListeners() {}

    /**
     * is used to register events listeners for OsImageServerPrefs as well as for OsUpgradePlanPrefs
     * @param osPrefsEntity - instance of Prefs Entity
     * @param onEntityReplace - on ReplacedByEntity event callback
     * @private
     */
    _registerOsPrefsListeners(osPrefsEntity, onEntityReplace) {
        const onPristinePropChanged = this._onPristinePropertiesChanged.bind(this, osPrefsEntity);

        osPrefsEntity
            .on("Initialized", this._triggerDataUpdated)
            .on("CreateCompleted", this._triggerDataUpdated)
            .on("PristinePropertiesChanged", onPristinePropChanged)
            .on("DirtyStateChanged", this._onPrefsDirtyStateChange)
            .once("ReplacedByEntity", onEntityReplace)
            .once("ShutdownStarted", () => {
                osPrefsEntity
                    .removeListener("Initialized", this._triggerDataUpdated)
                    .removeListener("CreateCompleted", this._triggerDataUpdated)
                    .removeListener("PristinePropertiesChanged", onPristinePropChanged)
                    .removeListener("DirtyStateChanged", this._onPrefsDirtyStateChange);
            })
            .once("ShutdownComplete", () => {
                osPrefsEntity.removeListener("ReplacedByEntity", onEntityReplace);
            });
    }

    loadOrganizationData() {
        return this._OrganizationService.getById({id: this.activeOrgId}).then((orgData) => {
            this._organizationData = orgData;
            this._triggerOrganizationDataChanged(orgData);
            return orgData;
        });
    }

    loadVendorConfig() {
        return this._vendorApiConfigEntity.loadVendorApiConfig(this.activeOrgId).then((config) => {
            // this._vendorApiConfig = config;
            this._isCiscoEnabled = _.get(config, "properties.vendors.cisco.enabled", false);
        });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenModelIsReady() {
        if (!_.isUndefined(this.activeOrgId)) {
            return Promise.all([
                this._imageServersFeed.whenInitialized(),
                this.getOsUpgradePlanPrefs(),
                this.getOsImageServerPrefs(),
                this.getMyUser(),
                this.loadOrganizationData(),
                this.loadVendorConfig(),
                this.getOsUpgradeOptions(),
                this.getVendorOsMap(),
                this.loadCredentialName(),
            ]).then(() => {
                this._sshProps = {
                    sshKexAlgorithms:
                        this._osImageServerPrefs._pristineProperties.sshKexAlgorithms ||
                        SSH_KEX_DEF,
                    sshCiphers:
                        this._osImageServerPrefs._pristineProperties.sshCiphers || SSH_CIPHER_DEF,
                    sshMACs: this._osImageServerPrefs._pristineProperties.sshMACs || SSH_MAC_DEF,
                    sshProtocolOneEnabled:
                        this._osImageServerPrefs._pristineProperties.sshProtocolOneEnabled || false,
                };
                if (this._userHasPerms() && !this._getEnableDisableImageServerConfig()) {
                    if (!this._osImageServerPrefs.fileServersEnabled) {
                        this._osImageServerPrefs.fileServersEnabled = true;
                        this._osImageServerPrefs.update().then(() => {
                            this.enableFileServer(true);
                        });
                    }
                }
                this._triggerDataUpdated();
                this._triggerDirtyStateChanged();
            });
        } else {
            return Promise.reject();
        }
    }

    _userHasPerms() {
        if (this._userSession) {
            return this._userSession.authorizedActions.indexOf("osmPrefsWrite") >= 0;
        }
    }

    shutdown() {
        /**
         * @event OSManagementComponent#Shutdown
         */
        this.emit("Shutdown");
        this._unregisterListeners();
        this.removeAllListeners();
        return Promise.all([
            this._renderer.shutdown(),
            _.invoke(this._imageServersFeed, "shutdown"),
            _.invoke(this._osImageServerPrefs, "shutdown"),
            _.invoke(this._osUpgradePlanPrefs, "shutdown"),
        ]).then(
            () => {
                this._OsUpgradePlanPrefsEntity = undefined;
                this._OsImageServersEntity = undefined;
                this._OsImageServerPrefsEntity = undefined;
                this._userSession = undefined;
                this._modalUserFeedback = undefined;
                this._imageServersFeed = undefined;
                this._osImageServerPrefs = undefined;
                this._osUpgradePlanPrefs = undefined;
                this._logger = undefined;
                this._userService = undefined;
                this._renderer = undefined;
                this._orgId = null;
                this._vendorApiConfigEntity = undefined;
                this._isCiscoEnabled = undefined;
            },
            (err) => {
                console.log("Error shutting down CustomFieldsComponent: ", err);
            }
        );
    }

    get logger() {
        return this._logger;
    }

    get userFeedback() {
        return this._modalUserFeedback;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getOrganizationData() {
        return this._organizationData;
    }

    get activeOrgIsRoot() {
        return this._organizationData.providerId === null;
    }

    organizationChanged({id, name}) {
        if (this._orgId === id) {
            return;
        }
        this._orgId = id;
        this._triggerOrganization({id, name});

        this._establishImageServersFeed(id);
        this.loadOrganizationData();

        Promise.all([
            this.getOsImageServerPrefs(),
            this.getOsUpgradePlanPrefs(),
            this.loadVendorConfig(),
            this.getOsUpgradeOptions(),
            this.getVendorOsMap(),
        ]).then(() => {
            if (!this._getEnableDisableImageServerConfig()) {
                if (!this._osImageServerPrefs.fileServersEnabled) {
                    this._osImageServerPrefs.fileServersEnabled = true;
                    this._osImageServerPrefs.update();
                }
            }
            return this._triggerDataUpdated("OrgChange");
        });
    }

    deleteImageServer(serverId) {
        const orgId = this.activeOrgId;
        const OsImageServersEntity = this._OsImageServersEntity;

        return OsImageServersEntity.loadAscendOsImageServer(orgId, serverId, {
            isMain: true,
            identifier: true,
        })
            .then((serverEntity) => {
                return serverEntity.isMain || serverEntity.identifier === "MAIN"
                    ? deleteNotMain(serverEntity)
                    : Promise.resolve(serverEntity);
            })
            .then((serverEntity) => serverEntity.delete());

        function deleteNotMain(mainServer) {
            const query = {
                $or: [{isMain: {$ne: true}}, {identifier: {$ne: "MAIN"}}],
            };
            return OsImageServersEntity.loadAscendOsImageServers(orgId, query, {id: true})
                .then((servers) => Promise.all(servers.map((server) => server.delete())))
                .then(() => mainServer);
        }
    }

    loadServerActivityLog(osImageServer) {
        return osImageServer.loadRelated("audit", "OsImageServer@audit", false);
    }
    getActionsData(osImageServerActions) {
        return Promise.resolve(_.invokeMap(osImageServerActions, "toJSON"));
    }
    parseActionsData(osImageServer, actionsData) {
        //console.log(`actions list for ${osImageServer.name} (${osImageServer.identifier}) [dev only]\n\t`, actionsData);

        const headers = ["Type", "description", "Date (UTC)"];
        const title = [].concat(
            `${osImageServer.name} (${osImageServer.identifier}) Actions`,
            new Array(headers.length - 1).fill("")
        );
        const getDescriptions = (action) => {
            let descriptions = [];
            const createdBy = action.createdBy;
            switch (action.pristineProperties.activityType) {
                case "create":
                    descriptions.push(
                        `Image Server created by ${createdBy ? `user (${createdBy})` : "system"}.`
                    );
                    break;
                case "update":
                    if (createdBy) {
                        descriptions.push(
                            `Image Server updated by ${
                                createdBy ? `user (${createdBy})` : "system"
                            }.`
                        );
                    }
                    /* eslint-disable no-case-declarations */
                    let patch = _.get(action, ["pristineProperties", "patch"]);
                    let lastErrorPatch = _.find(patch, {path: "/lastErrorMsg"});
                    let statusPatch = _.find(patch, {path: "/status"});
                    /* eslint-enable no-case-declarations */
                    if (lastErrorPatch) {
                        let error = lastErrorPatch.value;
                        let prevError = _.get(action, [
                            "pristineProperties",
                            "before",
                            "lastErrorMsg",
                        ]);
                        if (prevError && _.isEmpty(error)) {
                            descriptions.push(`Image Server error cleared: ${prevError}.`);
                        } else {
                            descriptions.push(`Image Server error: ${error}.`);
                        }
                    }
                    if (statusPatch && statusPatch.value) {
                        let prevStatus = _.get(action, ["pristineProperties", "before", "status"]);
                        if (prevStatus) {
                            descriptions.push(
                                `Image Server status changed from ${prevStatus} to ${statusPatch.value}.`
                            );
                        } else {
                            descriptions.push(`Image Server status set to ${statusPatch.value}.`);
                        }
                    }
            }
            return descriptions;
        };

        const data = [];
        actionsData.forEach((action) => {
            let descriptions = getDescriptions(action);
            for (let description of descriptions) {
                data.push([
                    action.createdBy ? "User" : "System",
                    description,
                    action.createdOn
                        ? new Date(action.createdOn).toLocaleString(undefined, {
                              timeZone: "UTC",
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                          })
                        : "",
                ]);
            }
        });
        return [title, headers].concat(data);
    }

    saveCSVFile(data) {
        console.log("this._organizationData", this._organizationData);
        const fileName = this._fileNamer({
            solution: "SS",
            general: "FileServerActivity",
            specific: this._organizationData.name,
        });

        let file = new this._steveIo.File(fileName, "text/csv", stringifyCSV(data));
        this._steveIo.fileFactory.saveFile(file);
    }

    downloadServersActivityLogs() {
        const servers = this._imageServersFeed.getCacheAsArray();
        const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000; //30 days of milliseconds
        const query = {createdOn: {$gte: thirtyDaysAgo}};
        const getActivityLog = (server) =>
            server
                .loadRelatedAudits(query)
                .then(this.getActionsData)
                .then(this.parseActionsData.bind(null, server));

        return Promise.all(servers.map(getActivityLog))
            .then((serversActivityLogs) => {
                this.saveCSVFile([].concat(serversActivityLogs));
            })
            .catch((error) => {
                this._logger({error}, "downloadServersActions() Error");
                throw error;
            });
    }

    refreshFileServer(serverId) {
        return this._OsImageServersEntity.resetMainService(this.activeOrgId, serverId);
    }

    _triggerDataUpdated(type) {
        /**
         * @event OSManagementComponent#DataUpdated
         */
        this.emit("DataUpdated", type);
    }

    _triggerDirtyStateChanged() {
        /**
         * @event OSManagementComponent#DirtyState
         */
        const resultState =
            this._osImageServerPrefs.isDirty() || this._osUpgradePlanPrefs.isDirty();
        this.emit("DirtyState", resultState);
    }

    _triggerOrganization(org) {
        /**
         * @event OSManagementComponent#Organization
         * @param {{id: string, name: string}} - new organization parameters.
         */
        this.emit("Organization", org);
    }

    _triggerOrganizationDataChanged(orgData) {
        /**
         * @event OSManagementComponent#OrganizationDataChange
         * @param {Object} - new organization data.
         */
        this.emit("OrganizationDataChange", orgData);
    }

    getActiveOrgId() {
        return this._orgId;
    }

    get myUserInfo() {
        return this._myUserInfo;
    }

    getOsUpgradePlanPrefs() {
        return this._OsUpgradePlanPrefsEntity
            .retrievePrefs(this.activeOrgId)
            .then((osUpgradePlanPrefs) => {
                this._setOsUpgradePlanPrefs(osUpgradePlanPrefs);
                return osUpgradePlanPrefs;
            });
    }

    getOsImageServerPrefs() {
        return this._OsImageServerPrefsEntity
            .retrievePrefs(this.activeOrgId)
            .then((osImageServerPrefs) => {
                this._setOsImageServerPrefs(osImageServerPrefs);
                return osImageServerPrefs;
            });
    }

    getOsUpgradeOptions() {
        this._OsUpgradeOptionsEntity
            .loadOsUpgradeOptions(this.activeOrgId)
            .then((optionEntites) => {
                this._osmOptionEntities = optionEntites;
                this.generateOsmOptions();
            });
    }

    getVendorOsMap() {
        this._ValueMapEntity.loadVendorOsMapEntity(this.activeOrgId).then((entity) => {
            this._vendorOsMap = entity;
            return entity;
        });
    }

    async loadCredentialName() {
        const credEntity = await this._credentialsEntity.loadCredentials(
            this.activeOrgId,
            {credsType: "gluwareFileServerSecret"},
            {name: true}
        );
        this._credentialName = !_.isEmpty(credEntity) ? credEntity[0].getName() : "";
        return Promise.resolve();
    }

    generateOsmOptions() {
        if (this._osmOptionEntities.length === 0) {
            this._osmOptionsAvailable = false;
            return;
        }
        this._osmOptionsAvailable = true;
        this._osmOptions = {};
        this._osmOptionEntities.reduce((acc, ety) => {
            const vendor = ety.vendor;
            const os = ety.os;
            const jtype = ety.jtype;
            if (!acc[vendor]) {
                acc[vendor] = {};
            }
            if (!acc[vendor][os]) {
                acc[vendor][os] = {};
            }
            acc[vendor][os].jtype = jtype;
            return acc;
        }, this._osmOptions);
        // this.generateOsmOptionsVendorDDOpts();
    }

    generateOsmOptionsVendorDDOpts() {
        if (!this._osmOptionsAvailable) {
            return;
        }
        const vendorsList = Object.keys(this._osmOptions);
        const vendorMap = this._vendorOsMap.extractVendorMapFromVendorOsMap();
        const vendorDDopts = vendorsList.map((vendor) => {
            return {
                value: vendor,
                label: vendor === "*" ? "All" : vendorMap[vendor] || vendor,
            };
        });
        // vendorsList.forEach(vendor => this.generateOsmOptionsOsDDOpts(vendor));
        return vendorDDopts;
    }

    generateOsmOptionsOsDDOpts(vendor) {
        if (!this._osmOptionsAvailable) {
            return;
        }
        if (!vendor) {
            return {};
        }
        const vendorData = this._osmOptions[vendor];
        const osList = Object.keys(vendorData);
        const osMap = this._vendorOsMap.extractOsMapFromVendorOsMap();
        const osDDOpts = osList.map((os) => {
            return {
                value: os,
                label: os === "*" ? "All" : osMap[os] || os,
            };
        });
        return osDDOpts;
    }

    _establishImageServersFeed(orgId) {
        if (!orgId) {
            return;
        }
        const query = {};
        const fields = [
            "enabled",
            "name",
            "identifier",
            "ip",
            "status",
            "percentFull",
            "isMain",
            "updatedOn",
            "ipv6",
        ].reduce((fields, field) => Object.assign(fields, {[field]: true}), {});

        const sort = {
            /*createdOn: 1*/
        };

        if (this._imageServersFeed) {
            this._imageServersFeed.reestablishFeed(orgId, query, fields, sort);
        } else {
            this._imageServersFeed = this._OsImageServersEntity.startAscendOsImageServersFeed(
                orgId,
                query,
                fields,
                sort
            );
        }

        return this._imageServersFeed;
    }

    canGenerateKeys(id) {
        const userGroup = this._userSession.user.getProperty("actionGroupName");
        const entity = this._imageServersFeed.getEntityById(id);

        return [groupsAllowedToGenerateKeys.includes(userGroup), !entity.sshKeysShown].every(
            Boolean
        );
    }

    getKeys(id) {
        return this._OsImageServersEntity
            .createSshKeys(this.activeOrgId, id)
            .then((data) => {
                const entity = this._imageServersFeed.getEntityById(id);
                entity.setSshKeysShown().update();
                return data;
            })
            .catch((error) => {
                this._logger.warn(
                    {error},
                    `${this.constructor.name} getKeys(): createSshKeys error`
                );
                return Promise.reject(error);
            });
    }

    canEditImageServers() {
        return this.fileServersEnabled;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onPristinePropertiesChanged(prefsEntity, newPristine, oldPristine) {
        const actualPristineDelta = _.fromPairs(
            _.differenceWith(_.toPairs(newPristine), _.toPairs(oldPristine), _.isEqual)
        );
        const localDelta = _.fromPairs(
            _.differenceWith(
                _.toPairs(prefsEntity.getProperties()),
                _.toPairs(oldPristine),
                _.isEqual
            )
        );

        //update untouched fields and keep changes for changed fields
        prefsEntity.updatePropertiesFromJson(Object.assign({}, actualPristineDelta, localDelta));

        this._triggerDataUpdated();
    }

    _onPrefsDirtyStateChange() {
        this._triggerDirtyStateChanged();
    }

    _onOsUpgradePlanPrefsReplaced(newOsUpgradePrefs) {
        this._setOsUpgradePlanPrefs(newOsUpgradePrefs);
        this._triggerDataUpdated();
    }
    _onOsImageServerPrefsReplaced(newOsUpgradePrefs) {
        this._setOsImageServerPrefs(newOsUpgradePrefs);
        this._triggerDataUpdated();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    getMyUser() {
        let userRequest = {
            userId: this._userSession.getUser().getId(),
            descendantOrgActionGroupId: this._orgId,
        };
        return this._userService.get(userRequest).then(
            (myInfo) => {
                this._myUserInfo = myInfo;
                _.set(
                    this.defaultProperties,
                    "planPrefs.catalogCreationAuthGroup",
                    _.keys(myInfo.availableActionGroups)[0]
                );
            },
            (err) => {
                console.log("I am unable to request your user info.", err);
            }
        );
    }

    updatePrefs(data) {
        const serverPrefsKeys = Object.keys(this.defaultProperties.serverPrefs);
        const planPrefsKeys = Object.keys(this.defaultProperties.planPrefs);

        this._osImageServerPrefs
            .clearPendingPatches()
            .updatePropertiesFromJson(_.pick(data, serverPrefsKeys));

        this._osUpgradePlanPrefs
            .clearPendingPatches()
            .updatePropertiesFromJson(_.pick(data, planPrefsKeys));
    }

    enableFileServer(state) {
        return state
            ? this._OsImageServerPrefsEntity.enableFileServersForTenant(this.activeOrgId)
            : this._OsImageServerPrefsEntity.disableFileServersForTenant(this.activeOrgId);
    }

    isSSHDirty() {
        let isSSHDirty = false;
        if (this._osImageServerPrefs.pendingPatches) {
            const updatedSSHProps = {
                sshKexAlgorithms: this._osImageServerPrefs.sshKexAlgorithms || SSH_KEX_DEF,
                sshCiphers: this._osImageServerPrefs.sshCiphers || SSH_CIPHER_DEF,
                sshMACs: this._osImageServerPrefs.sshMACs || SSH_MAC_DEF,
                sshProtocolOneEnabled: this._osImageServerPrefs.sshProtocolOneEnabled || false,
            };
            Object.keys(this._sshProps).forEach((key) => {
                if (key !== "sshProtocolOneEnabled") {
                    const newval = [...updatedSSHProps[key]];
                    const oldval = [...this._sshProps[key]];
                    if (!_.isEqual(newval.sort(), oldval.sort())) {
                        isSSHDirty = true;
                    }
                } else {
                    if (
                        this._sshProps.sshProtocolOneEnabled !==
                        updatedSSHProps.sshProtocolOneEnabled
                    ) {
                        isSSHDirty = true;
                    }
                }
            });
        }
        return isSSHDirty;
    }

    saveUpgradePrefs() {
        if (this.isSSHDirty()) {
            //Show veil
            const msg =
                "Some encryption algorithms may expose security vulnerabilities but may be required by older devices or firmware.";
            return this._modalUserFeedback
                .displayConfirmation(msg, {
                    title: "Warning",
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Cancel",
                        type: "text",
                    },
                })
                .then(() => {
                    return this.savePreferences();
                })
                .catch(() => {
                    this._logger.warn("OS Management :: Data not saved :: User Rejected");
                    return Promise.reject();
                });
        }
        return this.savePreferences();
    }

    savePreferences() {
        return Promise.all([
            this._osImageServerPrefs.pendingPatches
                ? this._osImageServerPrefs.update()
                : this._osImageServerPrefs,
            this._osUpgradePlanPrefs.pendingPatches
                ? this._osUpgradePlanPrefs.update()
                : this._osUpgradePlanPrefs,
        ]);
    }

    resetPrefs() {
        this._osImageServerPrefs.clearPendingPatches();
        this._osUpgradePlanPrefs.clearPendingPatches();
    }

    _getEnableDisableImageServerConfig() {
        if (this._userSession && this._userSession._userEntity) {
            return !!_.get(
                this._userSession._userEntity.authorizedTenants,
                `${this.activeOrgId}.providerName`,
                false
            );
        }
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = OSManagementComponent;

OSManagementComponent.$groupName = "Organization";

OSManagementComponent.$displayName = "OS Manager";
OSManagementComponent.$license = ["GLUWARE", "OSM"];

OSManagementComponent.$inject = [
    "$teve.services.instance.Organization",
    "$teve.userSession",
    "$teve.logger",
    "$teve.ui.userFeedback",
    "$teve.entities.OsImageServerPrefs",
    "$teve.entities.OsUpgradePlanPrefs",
    "$teve.entities.OsImageServer",
    "$teve.services.instance.User",
    "$teve.io",
    "$teve.ui.UiUtils",
    "$teve.entities.VendorApiConfig",
    "$teve.entities.OsUpgradeOptions",
    "$teve.entities.ValueMap",
    "$teve.router",
    "$teve.entities.Credentials",
];
